import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { languages } from "../locals/languageCode";

const ParentContext = createContext(null);

export const useParentContext = () => {
  const context = useContext(ParentContext);
  if (!context) {
    throw new Error(
      "useParentContext must be used within a ParentContextProvider"
    );
  }
  return context;
};

const ParentContextProvider = ({ children }) => {
  const BackendUrl = process.env.REACT_APP_BACKEND_URL;

  const [parentState, setParentState] = useState(null);

  // 1. Bulletproof Initialization for childConsents
  const [childConsents, setChildConsents] = useState(() => {
    try {
      const saved = localStorage.getItem("childConsents");
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error reading childConsents from localStorage:", error);
      return {};
    }
  });

  // 2. Bulletproof Initialization for hasParentConsent
  const [hasParentConsent, setHasParentConsent] = useState(() => {
    try {
      const saved = localStorage.getItem("hasParentConsent");
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.error("Error reading hasParentConsent from localStorage:", error);
      return false;
    }
  });

  const [language, setLanguage] = useState("en");
  const [googleTranslateReady, setGoogleTranslateReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 3. Reliable persistence for childConsents
  useEffect(() => {
    try {
      localStorage.setItem("childConsents", JSON.stringify(childConsents));
    } catch (error) {
      console.error("Error saving childConsents to localStorage:", error);
    }
  }, [childConsents]);

  // 4. Reliable persistence for hasParentConsent
  useEffect(() => {
    try {
      localStorage.setItem("hasParentConsent", JSON.stringify(hasParentConsent));
    } catch (error) {
      console.error("Error saving hasParentConsent to localStorage:", error);
    }
  }, [hasParentConsent]);

  // 5. Hard Reset Function for Logout
  const resetParentContext = () => {
    setParentState(null);
    setChildConsents({});
    setHasParentConsent(false);
    
    // Explicitly wipe the local storage keys so old data never leaks
    try {
      localStorage.removeItem("childConsents");
      localStorage.removeItem("hasParentConsent");
    } catch (error) {
      console.error("Error clearing localStorage on reset:", error);
    }
  };

const checkParentConsentStatus = async (parentId) => {
    if (!parentId) return false;
    
    const token = localStorage.getItem("s2stoken");

    try {
      const response = await axios.get(
        `${BackendUrl}/api/check_parent_consent/${parentId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true, 
        }
      );
      
      if (response?.data?.success && response?.data?.parent_consent) {
        setHasParentConsent(true);
        setChildConsents(response?.data?.optional_consent_scope || {});
        return true;
      } else {
        setHasParentConsent(false);
        return false;
      }
    } catch (error) {
      console.error("Error checking consent status:", error);
      return false;
    }
  };

  function requestForDoctor(child) {
    try {
      const response = axios.post(
        `${BackendUrl}/api/assign_doctor`,
        {
          parentId: parentState.parent_id,
          childId: child.child_id,
          firstName: parentState.parent_first_name,
          lastName: parentState.parent_last_name,
          phoneNumber: parentState.parent_phone_number,
          message: child.message,
        },
        {
          withCredentials: true,
        }
      );
      if (response?.data?.success) {
        toast.success("Doctor requested successfully.");
        navigate("/parent-profile");
      }
    } catch (error) {
      console.error("Error in requestForDoctor:", error);
      toast.error("An error occurred while requesting a doctor.");
    }
  }

  const restoreLanguageLabels = () => {
    languages.forEach((lang) => {
      const option = document.getElementById(`lang-opt-${lang.code}`);
      const option2 = document.getElementById(`lang-opt-${lang.label}`);
      const label = lang.label;

      if (option) {
        const plainText = option.textContent.trim();
        if (plainText !== label) {
          option.innerHTML = label;
          console.log(`Restored label for ${lang.code} (option)`);
        }
      }

      if (option2) {
        const plainText2 = option2.textContent.trim();
        if (plainText2 !== label) {
          option2.innerHTML = label;
          console.log(`Restored label for ${lang.code} (option2)`);
        }
      }
    });
  };

  const handleLanguageChange = (e) => {
    const languageCode = e.target.value;
    setLanguage(languageCode);
    console.log(`Attempting to change language to: ${languageCode}`);
    if (!googleTranslateReady) {
      console.log("Google Translate is not ready yet");
      return;
    }
    const selectField = document.querySelector(".goog-te-combo");
    if (selectField) {
      selectField.value = languageCode;
      selectField.dispatchEvent(new Event("change", { bubbles: true }));
      console.log(`Language changed to: ${languageCode}`);

      setTimeout(() => {
        restoreLanguageLabels();
      }, 1000);
    } else {
      console.log("Google Translate dropdown not found");
    }
  };

  useEffect(() => {
    if (!googleTranslateReady) return;

    const checkComboReady = setInterval(() => {
      const combo = document.querySelector(".goog-te-combo");
      if (combo) {
        clearInterval(checkComboReady);
        const defaultLang = "en";
        // Optional: re-run restore logic after change
        setTimeout(() => {
          handleLanguageChange({ target: { value: defaultLang } });
          restoreLanguageLabels();
        }, 1000);
      }
    }, 100); // poll every 100ms

    return () => clearInterval(checkComboReady);
  }, [googleTranslateReady]);

  useEffect(() => {
    setTimeout(() => {
      restoreLanguageLabels();
    }, 1000);
  }, [location.pathname]);

  useEffect(() => {
    const checkGoogleTranslate = setInterval(() => {
      if (window.google && window.google.translate) {
        clearInterval(checkGoogleTranslate);
        setGoogleTranslateReady(true);
        console.log("Google Translate is ready");
      }
    }, 100);

    return () => clearInterval(checkGoogleTranslate);
  }, []);

  return (
    <ParentContext.Provider
      value={{
        parentState,
        setParentState,
        childConsents,
        setChildConsents,
        hasParentConsent,
        setHasParentConsent,
        resetParentContext,
        requestForDoctor,
        language,
        handleLanguageChange,
        checkParentConsentStatus
      }}
    >
      {children}
    </ParentContext.Provider>
  );
};

export default ParentContextProvider;